/**
 * Realtime channel — the client half of Lane F (design §4.1). A thin typed
 * `EventSource` wrapper over the server's `GET /api/events` SSE stream
 * (docs/realtime.md): one multiplexed, session-authed (cookie) stream per tab.
 *
 * Responsibilities beyond raw `EventSource`:
 *   - **Named-event dispatch:** map SSE `event:` names (`forecast.created`,
 *     `contract.resolved`) to handlers, JSON-parsing `data` (a hint `{qkey, ts}`).
 *   - **Visibility pause:** close the stream when the tab is hidden and reopen
 *     when it becomes visible again, so background tabs don't hold connections.
 *   - **Reconnect:** `EventSource` auto-reconnects on transient errors (Cloud Run
 *     request-timeout severs are normal here); we only surface status.
 *   - **Resync on (re)open:** because the channel has NO replay buffer
 *     (docs/realtime.md — `Last-Event-ID` is protocol-only), an event that fires
 *     while the stream is down (a hidden tab, or a severed connection mid-reconnect)
 *     is lost if no later event arrives. So `onReopen` fires on every
 *     re-establishment of the stream (visibility reopen AND `EventSource`'s own
 *     auto-reconnect — both surface as a fresh `onopen`), letting the caller
 *     read-through and reconcile the missed window. It does NOT fire on the first
 *     open (the app's on-load hydration already covers that).
 *
 * No React imports — the hook (`useRealtimeChannel`) is the effect wrapper.
 */

import { createStreamTrace } from "../lib/telemetry/streamTrace.js";
import { routeOf } from "./netCapture.js";

export type RealtimeStatus = "open" | "error" | "closed";

export interface RealtimeChannelOpts {
  /** SSE endpoint. Default `"/api/events"`. */
  url?: string;
  /** Event-name → handler. `data` is the JSON-parsed frame payload (or null). */
  handlers: Record<string, (data: unknown) => void>;
  /** Optional connection-status callback (telemetry / UI dot). */
  onStatus?: (status: RealtimeStatus) => void;
  /**
   * Called on every RE-establishment of the stream (not the first open): a
   * visibility reopen or an `EventSource` auto-reconnect. The caller uses it to
   * reconcile any window missed while the stream was down (read-through) — the
   * channel has no replay buffer.
   */
  onReopen?: () => void;
  /** Close while the tab is hidden, reopen on visible. Default `true`. */
  pauseWhenHidden?: boolean;
}

export interface RealtimeChannel {
  start(): void;
  stop(): void;
}

/**
 * Create (but do not open) a realtime channel. Call `start()` to connect and
 * `stop()` to tear down (idempotent). Safe in non-browser/test environments
 * where `EventSource` is absent — `start()` is then a no-op.
 */
export function createRealtimeChannel(opts: RealtimeChannelOpts): RealtimeChannel {
  const { url = "/api/events", handlers, onStatus, onReopen, pauseWhenHidden = true } = opts;
  let es: EventSource | null = null;
  let stopped = false;
  // Tracks whether the visibilitychange listener is currently attached, so a
  // repeated `start()` (without an intervening `stop()`) can't stack duplicate
  // listeners — which would cause reopen/close churn on the next visibility flip.
  let listening = false;
  // Whether the stream has opened at least once this lifecycle. `onReopen` fires
  // on the 2nd+ open (a visibility reopen or an auto-reconnect), never the first.
  let openedOnce = false;
  // The telemetry `stream` tracer of the current connection (lib/telemetry/streamTrace.js);
  // a no-op while the net group is off.
  let trace: ReturnType<typeof createStreamTrace> | null = null;

  function open(): void {
    if (es || stopped) return;
    if (typeof EventSource === "undefined") return; // no SSE (SSR/tests) — inert
    // withCredentials so the Zitadel session cookie rides the stream (the /api
    // gate re-checks req.session.userId).
    const source = new EventSource(url, { withCredentials: true });
    source.onopen = () => {
      trace?.end();
      trace = createStreamTrace({ stream: "realtime" });
      trace.start({ route: routeOf(url) });
      onStatus?.("open");
      // Fire onReopen on every re-establishment (visibility reopen OR an
      // EventSource auto-reconnect — both surface as a fresh onopen), never the
      // first open. This closes the no-replay missed-window gap.
      if (openedOnce) onReopen?.();
      else openedOnce = true;
    };
    // EventSource auto-reconnects after an error; surface the state only.
    source.onerror = () => {
      // A failed handshake or reconnect errors before any open: trace that attempt too
      // (the fetch capture never sees /api/events).
      (trace ?? createStreamTrace({ stream: "realtime" })).error(new Error("eventsource error"));
      trace = null;
      onStatus?.("error");
    };
    for (const [name, fn] of Object.entries(handlers)) {
      source.addEventListener(name, (ev: MessageEvent) => {
        // The event-data payload's UTF-8 size.
        trace?.frameOf(name, ev.data);
        let data: unknown = null;
        try {
          data = ev.data ? JSON.parse(ev.data) : null;
        } catch {
          data = null; // malformed frame — hand the handler null, never throw
        }
        fn(data);
      });
    }
    es = source;
  }

  function close(): void {
    if (!es) return;
    es.close();
    es = null;
    trace?.end();
    trace = null;
    onStatus?.("closed");
  }

  function onVisibility(): void {
    if (typeof document === "undefined") return;
    if (document.visibilityState === "hidden") close();
    else if (!stopped) open();
  }

  function start(): void {
    stopped = false;
    if (pauseWhenHidden && typeof document !== "undefined") {
      if (!listening) {
        document.addEventListener("visibilitychange", onVisibility);
        listening = true;
      }
      // Don't open a connection for a tab that starts hidden; the visibility
      // handler opens it once the tab is shown. Mark the lifecycle as already
      // opened so that first visible connection is treated as a RE-open (fires
      // onReopen → resync): the stream was down for the whole hidden window, so an
      // event that landed between mount-hydration and first visibility would
      // otherwise be missed (no-replay).
      if (document.visibilityState === "hidden") {
        openedOnce = true;
        return;
      }
    }
    open();
  }

  function stop(): void {
    stopped = true;
    if (listening && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibility);
      listening = false;
    }
    // Reset so a reused instance (stop → start) begins a fresh lifecycle: the
    // next onopen is a genuine first open (no onReopen/resync), matching the
    // documented contract.
    openedOnce = false;
    close();
  }

  return { start, stop };
}
